import { type FC } from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { getNameForShow } from '@/common/lib/get-name-for-show';
import {
  type LotteryRewardName,
  type LotteryWinnerInfo,
  type TicketRewardType,
} from '@/common/types/lottery';
import { StringMain, StringSmall } from '@/components/Typography/Typography';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { Picture } from '@/components/ui/Picture/Picture';
import { TicketIcon } from '@/components/ui/TicketIcon/TicketIcon';
import { useAppSelector } from '@/redux/store';

import { animateDuration, ticketRewardToTier } from '../config';
import { useCurrentLotteryTier } from '../hooks';

import css from './styles.module.scss';

import usdPng from '/img/icons/usd-v2.png';
import usdWebp from '/img/icons/usd-v2.webp';
import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

const WinnerItem: FC<LotteryWinnerInfo> = ({
  userId,
  ticket,
  drawRank,
  avatar,
  firstName,
  lastName,
  userName,
  drawResult,
}) => {
  const isAnimationActive = useAppSelector(
    (state) => state.lottery.isLotteryHappeningNow,
  );
  const myUserId = useAppSelector((state) => state.user.user?.id);
  const currentTierName = useCurrentLotteryTier();
  const rewardName = drawResult
    ? (Object.keys(drawResult)[0] as LotteryRewardName)
    : null;

  const images: Record<
    'usdt' | 'tgem',
    { img: string; webp: string; alt: string }
  > = {
    usdt: {
      img: usdPng,
      webp: usdWebp,
      alt: 'usd',
    },
    tgem: {
      img: tgemPng,
      webp: tgemWebp,
      alt: 'tgem',
    },
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: isAnimationActive ? animateDuration : 0 }}
      className={cn(
        css.winnerItem,
        userId === myUserId && css['winnerItem--myTicket'],
      )}
    >
      <div className="w-[24px] flex items-center justify-start shrink-0">
        <StringSmall weight="bold" className="!leading-none">
          {drawRank}
        </StringSmall>
      </div>
      {avatar && <Avatar jpg={avatar} size={36} className="mr-2" />}
      <StringSmall className="whitespace-nowrap overflow-hidden mr-6 text-ellipsis flex-1 min-w-0 basis-0 leading-none">
        {getNameForShow({ lastName, firstName, userName })}
      </StringSmall>
      <TicketIcon
        type={currentTierName}
        className="size-[24px] shrink-0 mr-1"
      />
      <StringSmall weight="bold" className="mr-4 shrink-0">
        {ticket}
      </StringSmall>
      {rewardName && (
        <>
          {rewardName in images ? (
            <Picture
              className="size-[24px] shrink-0 mr-[2px]"
              {...images[rewardName as keyof typeof images]}
            />
          ) : (
            <TicketIcon
              type={ticketRewardToTier[rewardName as TicketRewardType]}
              className="size-[24px] shrink-0 mr-[2px]"
            />
          )}
          <StringMain weight="bold">{drawResult[rewardName]}</StringMain>
        </>
      )}
    </motion.div>
  );
};

export { WinnerItem };
